import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Avatar,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Calculate,
  Send,
  ListAlt,
  Info,
  CreditCard,
  Star,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CreditService from '../../services/creditService';
import DiscountService from '../../services/discountService';
import InterestRateService from '../../services/interestRateService';
import UserSpecificInterestRateService from '../../services/userSpecificInterestRateService';
import UserService from '../../services/userService';
import useResolvedUserId from '../../hooks/useResolvedUserId';

const sanitizeIban = (iban) =>
  iban ? iban.replace(/[^A-Za-z0-9]/g, '').toUpperCase() : '';

const formatIbanForDisplay = (iban) => {
  const sanitized = sanitizeIban(iban);
  if (!sanitized) return '';
  return sanitized.replace(/(.{4})/g, '$1 ').trim();
};

const resolveSellerIban = (_credit, user) => sanitizeIban(user?.ibanNumber);

const hasValidIban = (iban) => sanitizeIban(iban).length >= 15;

const CreateDiscount = ({ user }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const initialSellerIban = sanitizeIban(user?.ibanNumber);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState('');
  const [credits, setCredits] = useState([]);
  const [activeRates, setActiveRates] = useState(null);
  const [creditsWithDiscounts, setCreditsWithDiscounts] = useState(new Set());
  
  const [formData, setFormData] = useState({
    creditId: '',
    receivableAmount: '',
    dueDate: null,
    termDays: '',
    annualInterestRate: '',
    commissionRate: '',
    sellerIban: initialSellerIban,
  });
  
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [calculationResult, setCalculationResult] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [contractAccepted, setContractAccepted] = useState(false);
  const [showContract, setShowContract] = useState(false);
  const currentUserId = useResolvedUserId(user);

  // Fetch discounts by seller to check which credits have approved discounts
  const fetchDiscounts = async (sellerUserId) => {
    if (!sellerUserId) return new Set();
    
    try {
      const result = await DiscountService.getDiscountsBySeller(sellerUserId);
      
      if (result.success && result.data) {
        // Sadece onaylanmış (Approved) iskontosu olan kredi ID'lerini topla
        // Diğer durumlar (Pending, Rejected, Cancelled) veya hiç iskonto yoksa kredi görünsün
        const approvedDiscountCreditIds = new Set();
        result.data.forEach(discount => {
          const status = discount.status;
          const isApproved = status === 'Approved' || status === 2 || status === '2';
          
          if (isApproved) {
            approvedDiscountCreditIds.add(discount.creditId?.toString());
          }
        });
        return approvedDiscountCreditIds;
      }
    } catch (error) {
      console.error('Error fetching discounts:', error);
    }
    
    return new Set();
  };

  // Fetch active credits for seller
  const fetchCredits = async (sellerUserId) => {
    if (!sellerUserId) return;
    
    setLoading(true);
    setError('');
    
    // Önce indirimleri çek
    const discountCreditIds = await fetchDiscounts(sellerUserId);
    setCreditsWithDiscounts(discountCreditIds);
    
    const result = await CreditService.getCreditsBySeller(sellerUserId);
    
    if (result.success) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const filteredCredits = result.data.filter(c => {
        const statusValue = (c.status ?? '').toString().toLowerCase();
        const numericStatus = Number(c.status);
        const isApprovedStatus =
          statusValue === 'active' ||
          statusValue === 'approved' ||
          numericStatus === 2;
        const creditIdKey = c.id?.toString();
        const hasApprovedDiscount = creditIdKey ? discountCreditIds.has(creditIdKey) : false;
        const isGuaranteed = c.isGuaranteed === true || c.isGuaranteed === 'true' || c.isGuaranteed === 1;
        
        // Check if validity date is in the future (or today)
        let hasValidDueDate = false;
        if (c.validityDate) {
          const validityDate = new Date(c.validityDate);
          validityDate.setHours(0, 0, 0, 0);
          // Due date must be today or in the future for discount
          hasValidDueDate = validityDate >= today;
        }
        
        // Only show guaranteed, active credits without approved discount and with future due date
        return isApprovedStatus && !hasApprovedDiscount && isGuaranteed && hasValidDueDate;
      });
      setCredits(filteredCredits);
    } else {
      setError(result.message);
      setCredits([]);
    }
    
    setLoading(false);
  };

  // Fetch active interest rates (check user-specific first, then general)
  const fetchActiveRates = async (sellerUserId) => {
    // First check if user has a specific rate
    if (sellerUserId) {
      const userSpecificResult = await UserSpecificInterestRateService.getCurrentRateByUserId(sellerUserId);
      if (userSpecificResult.success && userSpecificResult.data) {
        // User has a specific rate, use it
        setActiveRates({
          rate: userSpecificResult.data.rate,
          nakdiCommissionRate: userSpecificResult.data.nakdiCommissionRate,
          gayrinakdiCommissionRate: userSpecificResult.data.gayrinakdiCommissionRate,
          isUserSpecific: true // Flag to indicate this is user-specific
        });
        return;
      }
    }
    
    // No user-specific rate, use general rate
    const result = await InterestRateService.getCurrentRate();
    if (result.success && result.data) {
      setActiveRates({
        ...result.data,
        isUserSpecific: false
      });
    }
  };

  // Fetch user profile to get IBAN
  const fetchUserProfile = async () => {
    try {
      const result = await UserService.getCurrentUserProfile();
      if (result.success && result.data) {
        const profile = result.data.data || result.data;
        const profileIban = sanitizeIban(profile.ibanNumber);
        if (profileIban) {
          setFormData(prev => ({
            ...prev,
            sellerIban: profileIban,
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  useEffect(() => {
    if (!currentUserId) return;

    fetchCredits(currentUserId);
    fetchActiveRates(currentUserId);
    fetchUserProfile();
  }, [currentUserId]);

  // Kredi seçildiğinde veya aktif oranlar geldiğinde otomatik güncelle
  useEffect(() => {
    if (formData.creditId && activeRates) {
      setFormData(prev => ({
        ...prev,
        annualInterestRate: activeRates.rate ? activeRates.rate.toString() : '',
        commissionRate: activeRates.nakdiCommissionRate ? activeRates.nakdiCommissionRate.toString() : '',
      }));
    }
  }, [activeRates, formData.creditId]);

  // Handle credit selection
  const handleCreditChange = (event) => {
    const creditId = event.target.value;
    const credit = credits.find(
      (c) => c.id === creditId || c.id?.toString() === creditId?.toString()
    );
    const normalizedCreditId = credit ? credit.id : creditId;
    
    if (credit) {
      // Vade tarihi krediden al
      const validityDate = credit.validityDate ? new Date(credit.validityDate) : null;
      
      // Vade süresini hesapla (bugünden vade tarihine kadar gün olarak)
      let termDays = '';
      if (validityDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        validityDate.setHours(0, 0, 0, 0);
        
        if (validityDate > today) {
          const daysDiff = Math.max(1, Math.ceil((validityDate - today) / (1000 * 60 * 60 * 24)));
          termDays = daysDiff.toString();
        } else {
          termDays = '1'; // Minimum 1 gün
        }
      }
      
      setSelectedCredit(credit);
      // IBAN'ı formData'dan al (zaten profil verisinden yüklenmiş olmalı)
      setFormData(prev => ({
        ...prev,
        creditId: normalizedCreditId,
        receivableAmount: credit.amount.toString(),
        dueDate: validityDate,
        termDays: termDays,
        // sellerIban zaten profil verisinden yüklenmiş, değiştirme
        // Faiz ve komisyon oranları aktif oranlardan otomatik gelir (değiştirilemez)
        annualInterestRate: activeRates?.rate ? activeRates.rate.toString() : '',
        commissionRate: activeRates?.nakdiCommissionRate ? activeRates.nakdiCommissionRate.toString() : '',
      }));
    } else {
      setSelectedCredit(null);
      setFormData(prev => ({
        ...prev,
        creditId: creditId,
        receivableAmount: '',
        dueDate: null,
        termDays: '',
        // sellerIban'ı koru, değiştirme
      }));
    }
    
    setCalculationResult(null);
  };

  // IBAN zaten fetchUserProfile ile yüklendi, bu useEffect'e gerek yok

  // Calculate discount
  const handleCalculate = async () => {
    if (!formData.creditId || !selectedCredit) {
      toast.error('Please select a credit');
      return;
    }

    if (!formData.receivableAmount || !formData.dueDate || !formData.termDays) {
      toast.error('Credit information is missing');
      return;
    }

    if (!formData.annualInterestRate || !formData.commissionRate) {
      toast.error('Interest or commission rate not found. Please contact admin.');
      return;
    }

    if (!hasValidIban(formData.sellerIban)) {
      toast.error('Your IBAN not found. Please add it in the Profile page before proceeding.');
      return;
    }

    const sellerUserId = currentUserId;
    if (!sellerUserId) {
      toast.error('User information could not be loaded. Please refresh the page.');
      return;
    }

    setCalculating(true);
    setError('');

    try {
      const dueDate = new Date(formData.dueDate);
      
      const discountData = {
        creditId: formData.creditId,
        sellerId: sellerUserId,
        buyerId: selectedCredit.buyerId,
        receivableAmount: parseFloat(formData.receivableAmount),
        dueDate: dueDate.toISOString().split('T')[0],
        termDays: parseInt(formData.termDays),
        annualInterestRate: parseFloat(formData.annualInterestRate),
        commissionRate: parseFloat(formData.commissionRate),
        sellerIban: sanitizeIban(formData.sellerIban),
      };

      const result = await DiscountService.calculateDiscount(discountData);

      if (result.success) {
        setCalculationResult(result.data);
        // Generate contract data
        const sellerName = user.name && user.surname 
          ? `${user.name} ${user.surname}` 
          : user.companyName || user.email || 'Seller';
        const buyerName = selectedCredit.buyerName || 'Buyer';
        
        // Generate contract data
        const contract = generateContractContent(discountData, result.data, sellerName, buyerName);
        setContractData(contract);
        setShowContract(true);
        setContractAccepted(false); // Yeni hesaplamada onayı sıfırla
      } else {
        setError(result.message);
        setCalculationResult(null);
        setShowContract(false);
      }
    } catch (error) {
      setError('An error occurred during calculation: ' + error.message);
      setCalculationResult(null);
      setShowContract(false);
    }

    setCalculating(false);
  };

  // Generate contract text - Returns JSX structure
  const generateContractContent = (data, calculation, sellerName, buyerName) => {
    const today = new Date();
    const dueDate = new Date(data.dueDate);
    const currency = selectedCredit?.currency || 'EUR';
    
    return {
      contractId: data.creditId,
      date: today.toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' }),
      sellerName,
      sellerIban: formatIbanForDisplay(data.sellerIban),
      buyerName,
      receivableAmount: parseFloat(data.receivableAmount).toFixed(2),
      dueDate: dueDate.toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' }),
      termDays: data.termDays,
      annualInterestRate: parseFloat(calculation.annualInterestRate).toFixed(2),
      commissionRate: parseFloat(calculation.commissionRate).toFixed(2),
      transactionFee: parseFloat(calculation.transactionFee).toFixed(2),
      commission: parseFloat(calculation.commission).toFixed(2),
      netPayment: parseFloat(calculation.netPayment).toFixed(2),
      currency,
    };
  };

  // Submit discount request
  const handleSubmit = async () => {
    if (!calculationResult) {
      toast.error('Please calculate first');
      return;
    }

    if (!contractAccepted) {
      toast.error('Please approve the contract');
      return;
    }

    if (!formData.creditId || !selectedCredit) {
      toast.error('Please select a credit');
      return;
    }

    if (!formData.receivableAmount || !formData.dueDate || !formData.termDays) {
      toast.error('Credit information is missing');
      return;
    }

    if (!formData.annualInterestRate || !formData.commissionRate) {
      toast.error('Interest or commission rate not found. Please contact admin.');
      return;
    }

    if (!hasValidIban(formData.sellerIban)) {
      toast.error('Your IBAN not found. Please add it in the Profile page before proceeding.');
      return;
    }

    const sellerUserId = currentUserId;
    if (!sellerUserId) {
      toast.error('User information could not be loaded. Please refresh the page.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const dueDate = new Date(formData.dueDate);
      
      const discountData = {
        creditId: formData.creditId,
        sellerId: sellerUserId,
        buyerId: selectedCredit.buyerId,
        receivableAmount: parseFloat(formData.receivableAmount),
        dueDate: dueDate.toISOString().split('T')[0],
        termDays: parseInt(formData.termDays),
        annualInterestRate: parseFloat(formData.annualInterestRate),
        commissionRate: parseFloat(formData.commissionRate),
        sellerIban: sanitizeIban(formData.sellerIban),
        contractAccepted: contractAccepted,
      };

      const result = await DiscountService.createDiscount(discountData);

      if (result.success) {
        toast.success('Discount request created successfully');
        navigate('/user/discount/list');
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('An error occurred while creating discount request: ' + error.message);
    }

    setLoading(false);
  };

  // Format currency
  const formatCurrency = (amount, currency = 'EUR') => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const parts = formatter.formatToParts(amount);
    let formatted = '';
    for (const part of parts) {
      if (part.type === 'currency') {
        formatted += part.value + ' ';
      } else {
        formatted += part.value;
      }
    }
    return formatted.trim();
  };


  const hasSellerIban = hasValidIban(formData.sellerIban);
  const sellerIbanDisplay = formatIbanForDisplay(formData.sellerIban);
  const creditSelectHelperText = credits.length === 0
    ? 'No eligible guaranteed payments found. Only guaranteed payments can be discounted.'
    : 'Choose the guaranteed payment you want to discount';

  return (
    <Box sx={{ minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ 
                  width: 64, 
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  border: '3px solid rgba(255,255,255,0.3)',
                  fontSize: '1.5rem'
                }}>
                  {user?.name && user?.surname ? 
                    `${user.name.charAt(0)}${user.surname.charAt(0)}`.toUpperCase() : 
                    user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  New Discount Loan
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Convert your future-dated receivables into cash with interest and commission deduction
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<ListAlt />}
                  onClick={() => navigate('/user/discount/list')}
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.3)',
                    }
                  }}
                >
                  Discount Loans
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>
            
            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Form */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                Discount Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fill in the discount details below.
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> Only guaranteed payments can be discounted. Non-guaranteed payments are not eligible for discount.
                </Typography>
              </Alert>
            </Box>

            <Grid container spacing={{ xs: 2, md: 3 }}>
              {/* Credit Selection */}
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Select Credit *"
                  value={formData.creditId}
                  onChange={handleCreditChange}
                  disabled={loading}
                  helperText={creditSelectHelperText}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) => {
                      if (!value) return 'Select a credit';
                      const credit = credits.find(c => c.id === value || c.id?.toString() === value?.toString());
                      if (!credit) return 'Select a credit';
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {formatCurrency(credit.amount, credit.currency)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              - {credit.buyerName || 'Buyer'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Star sx={{ fontSize: '0.75rem', color: '#FFC107' }} />
                            <Typography variant="caption" sx={{ color: '#FFC107', fontWeight: 600, fontSize: '0.65rem' }}>
                              Guaranteed
                            </Typography>
                          </Box>
                        </Box>
                      );
                    },
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: 360 },
                      },
                    },
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#1976d2' },
                      '&.Mui-focused fieldset': { borderColor: '#1976d2' },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CreditCard color={formData.creditId ? 'primary' : 'disabled'} fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                >
                  <MenuItem value="" disabled>
                    {loading ? 'Loading credits...' : 'Select a credit'}
                  </MenuItem>
                  {credits.length === 0 ? (
                    <MenuItem value="" disabled>
                      {loading ? 'Loading credits...' : 'No active credit found'}
                    </MenuItem>
                  ) : (
                    credits.map((credit) => (
                      <MenuItem key={credit.id} value={credit.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CreditCard color="primary" fontSize="small" />
                            <Typography variant="subtitle2" fontWeight={600}>
                              {formatCurrency(credit.amount, credit.currency)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              - {credit.buyerName || 'Buyer'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Star sx={{ fontSize: '0.875rem', color: '#FFC107' }} />
                            <Typography variant="caption" sx={{ color: '#FFC107', fontWeight: 600, fontSize: '0.7rem' }}>
                              Guaranteed
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>

              {/* Receivable Amount */}
              <Grid item xs={12} sm={6} lg={4}>
                <TextField
                  fullWidth
                  label="Receivable Amount *"
                  type="number"
                  value={formData.receivableAmount}
                  disabled={true}
                  InputProps={{
                    startAdornment: selectedCredit && (
                      <Typography sx={{ mr: 1 }}>
                        {selectedCredit.currency === 'EUR' ? '€' : selectedCredit.currency === 'USD' ? '$' : '₺'}
                      </Typography>
                    ),
                  }}
                  helperText="Automatically taken from credit"
                />
              </Grid>

              {/* Due Date */}
              <Grid item xs={12} sm={6} lg={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Due Date *"
                    value={formData.dueDate}
                    disabled={true}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        fullWidth 
                        helperText="Automatically taken from credit"
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Term (Days) */}
              <Grid item xs={12} sm={6} lg={4}>
                <TextField
                  fullWidth
                  label="Term (Days) *"
                  type="number"
                  value={formData.termDays}
                  disabled={true}
                  helperText="Automatically calculated from due date"
                  inputProps={{ step: "1", min: "1" }}
                />
              </Grid>

              {/* Annual Interest Rate */}
              <Grid item xs={12} sm={6} lg={4}>
                <TextField
                  fullWidth
                  label="Annual Interest Rate (%) *"
                  type="number"
                  value={formData.annualInterestRate}
                  disabled={true}
                  helperText={activeRates?.rate ? `Active rate: %${activeRates.rate} (Cannot be changed)` : 'Active rate not found'}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>

              {/* Commission Rate */}
              <Grid item xs={12} sm={6} lg={4}>
                <TextField
                  fullWidth
                  label="Commission Rate (%) *"
                  type="number"
                  value={formData.commissionRate}
                  disabled={true}
                  helperText={activeRates?.nakdiCommissionRate ? `Active rate: %${activeRates.nakdiCommissionRate} (Cannot be changed)` : 'Active rate not found'}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>

              {/* IBAN */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Your IBAN *"
                  value={sellerIbanDisplay}
                  placeholder="TR33 0006 1005 1978 6457 8413 26"
                  disabled
                  error={!hasSellerIban}
                  helperText={
                    hasSellerIban
                      ? 'Automatically loaded from your profile. Update it from the Profile page if necessary.'
                      : 'No IBAN on file. Please add your IBAN in the Profile page before creating a discount.'
                  }
                  InputProps={{
                    readOnly: true,
                  }}
                  FormHelperTextProps={{
                    sx: { color: hasSellerIban ? 'text.secondary' : 'error.main' },
                  }}
                />
              </Grid>

              {/* Calculate Button */}
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={calculating ? <CircularProgress size={20} /> : <Calculate />}
                  onClick={handleCalculate}
                  disabled={loading || calculating || !formData.creditId || !formData.receivableAmount || !formData.dueDate || !formData.annualInterestRate || !formData.commissionRate || !hasSellerIban}
                  size="large"
                  sx={{
                    height: { xs: 56, sm: 64 },
                    fontWeight: 600,
                  }}
                >
                  {calculating ? 'Calculating...' : 'Calculate'}
                </Button>
              </Grid>
            </Grid>

            {/* Calculation Result */}
            {calculationResult && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: 'text.primary' }}>
                  Calculation Results
                </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gap: { xs: 2, md: 3 },
                  gridTemplateColumns: {
                    xs: 'repeat(1, minmax(0, 1fr))',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(4, minmax(0, 1fr))',
                  },
                  gridAutoRows: '1fr',
                }}
              >
                {[
                  {
                    label: 'Receivable Amount',
                    value: formatCurrency(calculationResult.receivableAmount, selectedCredit?.currency || 'EUR'),
                    color: 'text.primary',
                    gradient: false,
                  },
                  {
                    label: 'Transaction Fee',
                    value: `- ${formatCurrency(calculationResult.transactionFee, selectedCredit?.currency || 'EUR')}`,
                    color: 'error.main',
                    gradient: false,
                  },
                  {
                    label: 'Commission',
                    value: `- ${formatCurrency(calculationResult.commission, selectedCredit?.currency || 'EUR')}`,
                    color: 'error.main',
                    gradient: false,
                  },
                  {
                    label: 'Net Payment',
                    value: formatCurrency(calculationResult.netPayment, selectedCredit?.currency || 'EUR'),
                    color: 'white',
                    gradient: true,
                  },
                ].map((card, index) => (
                  <Paper
                    key={card.label}
                    elevation={card.gradient ? 4 : 2}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: card.gradient ? 'none' : '1px solid',
                      borderColor: card.gradient ? 'transparent' : 'divider',
                      background: card.gradient ? 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' : 'background.paper',
                      color: card.gradient ? 'white' : 'inherit',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, color: card.gradient ? 'rgba(255,255,255,0.85)' : 'text.secondary' }}
                    >
                      {card.label}
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={card.gradient ? 700 : 600}
                      sx={{ color: card.gradient ? 'white' : card.color }}
                    >
                      {card.value}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Contract Section */}
              {showContract && contractData && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
                    Receivable Assignment Contract
                  </Typography>
                  
                  <Paper 
                    elevation={2}
                    sx={{ 
                      p: { xs: 2, sm: 3, md: 4 }, 
                      bgcolor: 'background.paper', 
                      color: 'text.primary',
                      maxHeight: 500,
                      overflow: 'auto',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 3,
                    }}
                  >
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4, pb: 3, borderBottom: '2px solid', borderColor: 'divider' }}>
                      <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: 'primary.main' }}>
                        RECEIVABLE ASSIGNMENT CONTRACT
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Transaction ID: {contractData.contractId} | Date: {contractData.date}
                      </Typography>
                    </Box>

                    {/* Parties Section */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                        PARTIES
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gap: { xs: 2, sm: 2.5 },
                          gridTemplateColumns: {
                            xs: 'repeat(1, minmax(0, 1fr))',
                            sm: 'repeat(3, minmax(0, 1fr))',
                          },
                        }}
                      >
                        {[
                          {
                            title: '1. CREDITOR (SELLER)',
                            rows: [
                              { label: 'Name/Company:', value: contractData.sellerName },
                              { label: 'IBAN:', value: contractData.sellerIban },
                            ],
                          },
                          {
                            title: '2. ASSIGNEE (BANK)',
                            rows: [{ label: 'Name/Company:', value: 'DPG Finance Bank' }],
                          },
                          {
                            title: '3. DEBTOR (BUYER)',
                            rows: [{ label: 'Name/Company:', value: contractData.buyerName }],
                          },
                        ].map((party) => (
                          <Box
                            key={party.title}
                            sx={{
                              p: 2,
                              bgcolor: 'grey.50',
                              borderRadius: 1,
                              minHeight: 140,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                              {party.title}
                            </Typography>
                            {party.rows.map((row) => (
                              <Box key={`${party.title}-${row.label}`} sx={{ mb: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  {row.label}
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {row.value}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* Subject */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'primary.main' }}>
                        SUBJECT
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.8, color: 'text.primary' }}>
                        With this contract, the Creditor (Seller) assigns the receivable specified below to the Bank in an irrevocable (non-recourse) manner.
                      </Typography>
                    </Box>

                    {/* Receivable Info */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'primary.main' }}>
                        RECEIVABLE INFORMATION
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Receivable Amount
                            </Typography>
                            <Typography variant="h6" fontWeight={600}>
                              {contractData.receivableAmount} {contractData.currency}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Due Date
                            </Typography>
                            <Typography variant="h6" fontWeight={600}>
                              {contractData.dueDate}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Term (Days)
                            </Typography>
                            <Typography variant="h6" fontWeight={600}>
                              {contractData.termDays} days
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Discount Calculations */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'primary.main' }}>
                        DISCOUNT CALCULATIONS
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Annual Interest Rate
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              %{contractData.annualInterestRate}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Commission Rate
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              %{contractData.commissionRate}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Transaction Fee
                            </Typography>
                            <Typography variant="body1" fontWeight={600} color="error.main">
                              - {contractData.transactionFee} {contractData.currency}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Commission
                            </Typography>
                            <Typography variant="body1" fontWeight={600} color="error.main">
                              - {contractData.commission} {contractData.currency}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9 }}>
                              Net Payment
                            </Typography>
                            <Typography variant="h6" fontWeight={700}>
                              {contractData.netPayment} {contractData.currency}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Obligations */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: 'primary.main' }}>
                        OBLIGATIONS
                      </Typography>
                      <Box component="ol" sx={{ pl: 3, '& li': { mb: 1.5, lineHeight: 1.8 } }}>
                        <Typography component="li" variant="body2">
                          The Creditor (Seller) assigns this receivable to the Bank in an irrevocable (non-recourse) manner.
                        </Typography>
                        <Typography component="li" variant="body2">
                          The Creditor (Seller) cannot claim any rights related to this receivable and waives all rights to it.
                        </Typography>
                        <Typography component="li" variant="body2">
                          The Bank has taken over the receivable and will pay the Creditor the net payment amount ({contractData.netPayment} {contractData.currency}).
                        </Typography>
                        <Typography component="li" variant="body2">
                          The Debtor (Buyer) will make payment to the Bank on the due date ({contractData.dueDate}).
                        </Typography>
                        <Typography component="li" variant="body2">
                          By accepting this contract, the Creditor (Seller) is deemed to have fully assigned the receivable and has no right of return.
                        </Typography>
                        <Typography component="li" variant="body2">
                          This contract has been approved electronically and has legal validity.
                        </Typography>
                      </Box>
                    </Box>

                    {/* Signatures */}
                    <Box sx={{ mt: 4, pt: 3, borderTop: '2px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                        SIGNATURES
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                              Creditor (Seller)
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Name:</strong> {contractData.sellerName}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>IBAN:</strong> {contractData.sellerIban}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Date:</strong> {contractData.date}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: 'success.main', fontWeight: 600 }}>
                              ✓ Electronic Approval
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                              Bank
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Company:</strong> DPG Finance Bank
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Date:</strong> _______________
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Signature:</strong> _______________
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>

                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={contractAccepted}
                          onChange={(e) => setContractAccepted(e.target.checked)}
                          disabled={loading}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.primary">
                          <strong>I have read, understood and accept the contract.</strong> I accept to assign receivable to the bank in an irrevocable (non-recourse) manner.
                        </Typography>
                      }
                    />
                  </Box>
                </Box>
              )}

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                  onClick={handleSubmit}
                  disabled={loading || !contractAccepted}
                  size="large"
                  sx={{ mt: 3 }}
                >
                  {loading ? 'Creating...' : contractAccepted ? 'Create Discount Request' : 'Approve Contract'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default CreateDiscount;

